// src/components/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { APPLICATION_STATUS } from '../../constants/statusConstants';
import storageService from '../../services/storageService';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    companies: 0,
    jobs: 0,
    candidates: 0,
    placements: 0,
    interviews: 0
  });
  const [statusCounts, setStatusCounts] = useState({});
  const [recentPlacements, setRecentPlacements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = () => {
    setTimeout(() => {
      const companies = storageService.getCompanies();
      const jobs = storageService.getJobs();
      const candidates = storageService.getCandidates();
      const placements = storageService.getPlacements();
      const interviews = storageService.getInterviews();

      // Calculate status counts
      const counts = {};
      placements.forEach(p => {
        counts[p.status] = (counts[p.status] || 0) + 1;
      });

      // Get recent placements
      const recent = placements.slice(-5).map(p => {
        const job = storageService.getJob(p.jobId);
        const candidate = storageService.getCandidate(p.candidateId);
        const company = job ? storageService.getCompany(job.companyId) : null;
        return {
          ...p,
          jobTitle: job?.title,
          candidateName: candidate?.name,
          companyName: company?.name
        };
      }).filter(p => p.jobTitle).reverse();

      setStats({
        companies: companies.length,
        jobs: jobs.length,
        candidates: candidates.length,
        placements: placements.length,
        interviews: interviews.length
      });
      setStatusCounts(counts);
      setRecentPlacements(recent);
      setLoading(false);
    }, 500);
  };

  const getStatusColor = (status) => {
    const colors = {
      [APPLICATION_STATUS.RESUME_SENT]: 'status-resume',
      [APPLICATION_STATUS.INTERVIEW_IN_PROGRESS]: 'status-interview',
      [APPLICATION_STATUS.SELECTED]: 'status-selected',
      [APPLICATION_STATUS.JOINED]: 'status-joined',
      [APPLICATION_STATUS.REJECTED]: 'status-rejected'
    };
    return colors[status] || 'status-default';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-content">
          <h1>Welcome to JSK Placement CRM</h1>
          <p>Manage your recruitment pipeline efficiently</p>
        </div>
        {/* <div className="welcome-actions">
          <Link to="/companies/new" className="btn-primary">
            + Add Company
          </Link>
          <Link to="/jobs/new" className="btn-secondary">
            + Add Job
          </Link>
        </div> */}
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon company">🏢</div>
          <div className="stat-content">
            <h3>{stats.companies}</h3>
            <p>Total Companies</p>
          </div>
          <Link to="/companies" className="stat-link">View All →</Link>
        </div>

        <div className="stat-card">
          <div className="stat-icon job">💼</div>
          <div className="stat-content">
            <h3>{stats.jobs}</h3>
            <p>Job Requirements</p>
            <span className="stat-sub">{stats.jobs} Open Positions</span>
          </div>
          <Link to="/jobs" className="stat-link">View All →</Link>
        </div>

        <div className="stat-card">
          <div className="stat-icon candidate">👤</div>
          <div className="stat-content">
            <h3>{stats.candidates}</h3>
            <p>Candidates</p>
          </div>
          <Link to="/candidates" className="stat-link">View All →</Link>
        </div>

        <div className="stat-card">
          <div className="stat-icon placement">📋</div>
          <div className="stat-content">
            <h3>{stats.placements}</h3>
            <p>Placements</p>
            <span className="stat-sub">Active Applications</span>
          </div>
          <Link to="/placements" className="stat-link">View All →</Link>
        </div>
      </div>

      {/* Status Overview */}
      <div className="status-section">
        <h2>Placement Status Overview</h2>
        <div className="status-grid">
          {Object.values(APPLICATION_STATUS).map(status => (
            <div key={status} className="status-item">
              <div className={`status-indicator ${getStatusColor(status)}`}></div>
              <div className="status-details">
                <span className="status-label">{status}</span>
                <span className="status-count">{statusCounts[status] || 0}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="activity-section">
        <div className="section-header">
          <h2>Recent Placements</h2>
          <Link to="/placements" className="view-all">View All →</Link>
        </div>
        <div className="activity-table">
          <table>
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Job Position</th>
                <th>Company</th>
                <th>Status</th>
                <th>Applied Date</th>
              </tr>
            </thead>
            <tbody>
              {recentPlacements.length > 0 ? (
                recentPlacements.map(placement => (
                  <tr key={placement.id}>
                    <td>
                      <div className="cell-candidate">
                        <div className="candidate-avatar">
                          {placement.candidateName?.charAt(0)}
                        </div>
                        <span>{placement.candidateName}</span>
                      </div>
                    </td>
                    <td>{placement.jobTitle}</td>
                    <td>{placement.companyName}</td>
                    <td>
                      <span className={`status-badge ${getStatusColor(placement.status)}`}>
                        {placement.status}
                      </span>
                    </td>
                    <td>{placement.appliedDate}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="empty-row">
                    No placements found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h2>Quick Actions</h2>
        <div className="quick-actions-grid">
          <Link to="/companies/new" className="quick-action">
            <div className="action-icon">🏢</div>
            <h4>Add Company</h4>
            <p>Register new client company</p>
          </Link>
          <Link to="/jobs/new" className="quick-action">
            <div className="action-icon">💼</div>
            <h4>Post Job</h4>
            <p>Create job requirement</p>
          </Link>
          <Link to="/candidates/new" className="quick-action">
            <div className="action-icon">👤</div>
            <h4>Add Candidate</h4>
            <p>Register new candidate</p>
          </Link>
          <Link to="/interviews/new" className="quick-action">
            <div className="action-icon">🎯</div>
            <h4>Schedule Interview</h4>
            <p>Schedule new interview</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;